import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../Admin/constants/adminColors';
import AdminModal from '../Admin/components/AdminModal';
import AdminButton from '../Admin/components/AdminButton';
import { AdminFormField, AdminTextInput } from '../Admin/components/AdminFormField';
import LhDatePicker from '../../components/LhDatePicker';
import FontFamily from '../../constants/fonts';
import { MyTeamMember, TeamMemberRosterStatus } from './types';
import { fetchMyTeam, addEmployee, updateEmployee, lookupEmployeeByGpid } from './peopleHubApi';
import { formatLongDate } from './formatDate';
import { emailToDisplayName } from '../ActionPlan/utils/formOptions';
import { selectLocTypes, selectOrgTypes } from '../../store/slices/masterDataSlice';
import { useToast } from '../../components/LhToastContainer';

// Maps a /my-team teamMembers[] row (see PeoplePortService#buildRosterRow) to the UI shape.
const KNOWN_STATUSES: TeamMemberRosterStatus[] = ['Active', 'In Onboarding', 'On Leave', 'Inactive', 'Terminated'];

// Per-employee LCW metrics (cases/gapTime/cph/dps/bpa/ku) are not part of this endpoint's
// payload — they come from the employee profile/metrics-trend endpoints — so default to null.
// department/org aren't returned by this endpoint either — default to '—' for display.
const mapRosterRow = (row: Record<string, any>): MyTeamMember => ({
  id: String(row.employeeId ?? row.gpid),
  name: row.fullName ?? `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim(),
  gpid: String(row.gpid ?? ''),
  manager: row.manager ?? '',
  role: row.role ?? '',
  shift: row.shift ?? '',
  department: row.department ?? '',
  org: row.org ?? '',
  hireDate: row.hireDate && row.hireDate !== 'N/A' ? row.hireDate : '',
  seniority: row.tenure && row.tenure !== 'Unknown' ? row.tenure : '',
  seniorityOrder: row.seniorityOrder ?? 0,
  status: KNOWN_STATUSES.includes(row.status) ? row.status : 'Active',
  peerCoach: row.peerCoach ?? '',
  preferredLanguage: row.preferredLanguage ?? '',
  newHire: !!row.newHire,
  // Read-only, computed server-side (hireDate + location onboarding timeline) — never user-entered.
  expectedGraduationDate: row.expectedGraduationDate ?? null,
  cases: null,
  gapTime: null,
  cph: null,
  dps: null,
  bpa: null,
  ku: null,
  lastPulseCheck: row.lastPulseCheckDate && row.lastPulseCheckDate !== 'N/A' ? row.lastPulseCheckDate : null,
  lastObservation: row.lastObservationDate && row.lastObservationDate !== 'N/A' ? row.lastObservationDate : null,
});

// ─── Types ────────────────────────────────────────────────────────────────────
type SortKey = keyof MyTeamMember;
type SortDir = 'asc' | 'desc';
type GroupBy = 'none' | 'role' | 'manager';

// Metric column definition (mirrors the HTML `ln` array)
interface MetricCol {
  key: 'cases' | 'gapTime' | 'cph' | 'dps' | 'bpa' | 'ku';
  label: string;
  target: number | null;
  higherIsBetter: boolean;
  decimals: number;
}
const METRIC_COLS: MetricCol[] = [
  { key: 'cases',   label: 'Cases Picked', target: 1150, higherIsBetter: true,  decimals: 0 },
  { key: 'gapTime', label: 'Gap Time',     target: 400,  higherIsBetter: false, decimals: 0 },
  { key: 'cph',     label: 'CPH',          target: 150,  higherIsBetter: true,  decimals: 0 },
  { key: 'dps',     label: 'DPS%',         target: 100,  higherIsBetter: true,  decimals: 1 },
  { key: 'bpa',     label: 'BPA',          target: 1.25, higherIsBetter: true,  decimals: 2 },
  { key: 'ku',      label: 'KU%',          target: 70,   higherIsBetter: true,  decimals: 1 },
];

const isMetricBad = (col: MetricCol, val: number | null): boolean => {
  if (val === null || col.target === null) return false;
  return col.higherIsBetter ? val < col.target : val > col.target;
};

// ─── Icons ────────────────────────────────────────────────────────────────────
const PersonIcon = () => (
  <MaterialIcons name="assignment-ind" size={20} color={AdminColors.blue700} />
);

// ─── Status badge ─────────────────────────────────────────────────────────────
// Tag palette copied from the mockup's --nav-* tokens.
const STATUS_COLORS: Record<TeamMemberRosterStatus, { bg: string; color: string }> = {
  'Active':        { bg: '#CAF3C6', color: '#0C420B' },
  'In Onboarding': { bg: '#DAEEFA', color: '#034895' },
  'On Leave':      { bg: '#FCECDE', color: '#4D2E0C' },
  'Inactive':      { bg: '#E6E6E6', color: '#585858' },
  'Terminated':    { bg: '#FBE6E9', color: '#A10D26' },
};

const StatusBadge: React.FC<{ status: TeamMemberRosterStatus }> = ({ status }) => {
  const cfg = STATUS_COLORS[status] ?? STATUS_COLORS['Active'];
  return (
    <View style={[badgeStyles.wrap, { backgroundColor: cfg.bg }]}>
      <Text style={[badgeStyles.text, { color: cfg.color }]}>{status}</Text>
    </View>
  );
};
const badgeStyles = StyleSheet.create({
  wrap: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4, alignSelf: 'flex-start' },
  text: { fontSize: 11, lineHeight: 16, fontFamily: FontFamily.bold, fontWeight: '700' },
});

// ─── Filter Menu — Modal-based so it escapes ScrollView clipping ──────────────
// Uses the same visual design as PeopleHubHome's filterMenu styles.
interface FilterMenuProps {
  label: string;
  options: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  isOpen: boolean;
  onToggle: () => void;
}

const FilterMenu: React.FC<FilterMenuProps> = ({ label, options, selected, onChange, isOpen, onToggle }) => {
  const [query, setQuery] = useState('');
  // position of the dropdown panel (measured from trigger button)
  const [dropPos, setDropPos] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<View>(null);

  const filteredOptions = useMemo(
    () => options.filter((o) => o.toLowerCase().includes(query.trim().toLowerCase())),
    [options, query],
  );

  const toggleValue = (value: string) => {
    const next = new Set(selected);
    next.has(value) ? next.delete(value) : next.add(value);
    onChange(next);
  };

  const handleOpen = () => {
    if (triggerRef.current) {
      triggerRef.current.measureInWindow((x, y, _w, h) => {
        setDropPos({ x, y: y + h + 4 });
        onToggle();
      });
    } else {
      onToggle();
    }
  };

  return (
    <View ref={triggerRef}>
      <TouchableOpacity
        onPress={handleOpen}
        accessibilityLabel={`Filter by ${label}`}
        hitSlop={6}
      >
        <MaterialIcons
          name="filter-list"
          size={16}
          color={selected.size > 0 ? AdminColors.blue700 : AdminColors.gray350}
        />
      </TouchableOpacity>

      <Modal
        visible={isOpen}
        transparent
        animationType="none"
        onRequestClose={onToggle}
      >
        {/* Full-screen backdrop to close on outside tap */}
        <Pressable style={fmStyles.modalBackdrop} onPress={onToggle}>
          {/* Stop propagation so taps inside the panel don't close it */}
          <Pressable
            style={[fmStyles.menu, { top: dropPos.y, left: dropPos.x }]}
            onPress={e => e.stopPropagation()}
          >
            <View style={fmStyles.searchBox}>
              <MaterialIcons name="search" size={14} color={AdminColors.gray350} />
              <TextInput
                value={query}
                onChangeText={setQuery}
                placeholder="Search..."
                placeholderTextColor={AdminColors.gray350}
                style={fmStyles.searchInput}
                autoFocus={false}
              />
            </View>
            <View style={fmStyles.actionsRow}>
              <TouchableOpacity onPress={() => onChange(new Set(options))}>
                <Text style={fmStyles.actionText}>Select All</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => onChange(new Set())}>
                <Text style={fmStyles.actionText}>Clear</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={fmStyles.optionList} nestedScrollEnabled>
              {filteredOptions.map((option) => (
                <TouchableOpacity key={option} style={fmStyles.option} onPress={() => toggleValue(option)}>
                  <MaterialIcons
                    name={selected.has(option) ? 'check-box' : 'check-box-outline-blank'}
                    size={16}
                    color={selected.has(option) ? AdminColors.blue700 : AdminColors.gray350}
                  />
                  <Text style={fmStyles.optionText} numberOfLines={1}>{option}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <Text style={fmStyles.footerText}>{selected.size} of {options.length} selected</Text>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

const fmStyles = StyleSheet.create({
  // Full-screen transparent backdrop (inside the Modal)
  modalBackdrop: {
    flex: 1,
  },
  // Dropdown panel — positioned absolutely relative to the Modal layer
  menu: {
    position: 'absolute',
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    width: 240,
    paddingVertical: 10,
    paddingHorizontal: 10,
    gap: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 8,
  },
  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    borderWidth: 1, borderColor: AdminColors.borderDefault, borderRadius: 6,
    paddingHorizontal: 8, height: 30,
  },
  searchInput: {
    flex: 1, fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textPrimary,
    padding: 0, outlineWidth: 0, outline: 'none',
  } as any,
  actionsRow: { flexDirection: 'row', gap: 14 },
  actionText: { fontFamily: FontFamily.medium, fontSize: 12, color: AdminColors.blue700 },
  optionList: { maxHeight: 180 },
  option: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 5 },
  optionText: { fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textPrimary, flexShrink: 1 },
  footerText: {
    fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textMuted,
    borderTopWidth: 1, borderTopColor: AdminColors.borderDefault, paddingTop: 6,
  },
});

// ─── Sortable column header ───────────────────────────────────────────────────
interface SortableColProps {
  label: string;
  colKey: string;
  sortKey: string;
  sortDir: SortDir;
  onSort: (k: string) => void;
  filterOptions: string[];
  filterSelected: Set<string>;
  onFilterChange: (next: Set<string>) => void;
  openFilterCol: string | null;
  onFilterToggle: (k: string) => void;
  align?: 'left' | 'right';
}

const SortableCol: React.FC<SortableColProps> = ({
  label, colKey, sortKey, sortDir, onSort,
  filterOptions, filterSelected, onFilterChange,
  openFilterCol, onFilterToggle, align = 'left',
}) => {
  const active = sortKey === colKey;
  return (
    <View style={[thStyles.wrap, align === 'right' && thStyles.wrapRight]}>
      <TouchableOpacity
        onPress={() => onSort(colKey)}
        style={thStyles.labelWrap}
        activeOpacity={0.7}
      >
        <Text style={[thStyles.label, active && thStyles.labelActive]}>{label}</Text>
        <MaterialIcons
          name={active && sortDir === 'desc' ? 'arrow-downward' : 'arrow-upward'}
          size={12}
          color={active ? AdminColors.blue700 : AdminColors.gray350}
        />
      </TouchableOpacity>
      <FilterMenu
        label={label}
        options={filterOptions}
        selected={filterSelected}
        onChange={onFilterChange}
        isOpen={openFilterCol === colKey}
        onToggle={() => onFilterToggle(colKey)}
      />
    </View>
  );
};

const thStyles = StyleSheet.create({
  wrap: { flexDirection: 'row', alignItems: 'center', gap: 2 },
  wrapRight: { justifyContent: 'flex-end' },
  labelWrap: { flexDirection: 'row', alignItems: 'center', gap: 3 },
  // th: 10px bold uppercase, 0.5px tracking, secondary text (mockup .people-table th).
  label: {
    fontSize: 10, lineHeight: 14, fontWeight: '700', color: AdminColors.textSecondary,
    fontFamily: FontFamily.bold, textTransform: 'uppercase', letterSpacing: 0.5,
  },
  labelActive: { color: AdminColors.blue700 },
});

// ─── Inline picker (for employee form) ───────────────────────────────────────
// The options list is rendered in a Modal (measured from the trigger button),
// not as a position:absolute sibling — the form body is a ScrollView, which
// always clips overflowing content regardless of zIndex, so an in-place
// absolute dropdown gets clipped/covered by whatever renders after it. Modal
// escapes that clipping entirely (same pattern as FilterMenu above).
// Controlled by a shared activeField key (owned by the parent form) so only
// one picker's dropdown is open at a time.
interface InlinePickerProps {
  fieldId: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  placeholder?: string;
  activeField: string | null;
  onOpenChange: (id: string | null) => void;
}
const InlinePicker: React.FC<InlinePickerProps> = ({
  fieldId, value, options, onChange, placeholder, activeField, onOpenChange,
}) => {
  const open = activeField === fieldId;
  const [dropPos, setDropPos] = useState({ x: 0, y: 0, width: 0 });
  const triggerRef = useRef<View>(null);

  const handleOpen = () => {
    if (open) { onOpenChange(null); return; }
    if (triggerRef.current) {
      triggerRef.current.measureInWindow((x, y, w, h) => {
        setDropPos({ x, y: y + h + 4, width: w });
        onOpenChange(fieldId);
      });
    } else {
      onOpenChange(fieldId);
    }
  };

  return (
    <View ref={triggerRef} style={ipStyles.wrap}>
      <TouchableOpacity style={ipStyles.btn} onPress={handleOpen} activeOpacity={0.8}>
        <Text style={[ipStyles.val, !value && ipStyles.placeholder]} numberOfLines={1}>
          {value || placeholder || 'Choose…'}
        </Text>
        <Text style={ipStyles.arrow}>{open ? '▲' : '▼'}</Text>
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="none" onRequestClose={() => onOpenChange(null)}>
        <Pressable style={ipStyles.modalBackdrop} onPress={() => onOpenChange(null)}>
          <Pressable
            style={[ipStyles.dropdown, { top: dropPos.y, left: dropPos.x, width: dropPos.width }]}
            onPress={(e) => e.stopPropagation()}
          >
            <ScrollView style={ipStyles.optionList} nestedScrollEnabled>
              {options.map(opt => (
                <TouchableOpacity key={opt} style={[ipStyles.opt, opt === value && ipStyles.optActive]}
                  onPress={() => { onChange(opt); onOpenChange(null); }} activeOpacity={0.7}>
                  <Text style={[ipStyles.optText, opt === value && ipStyles.optTextActive]}>{opt}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};
const ipStyles = StyleSheet.create({
  wrap: { position: 'relative' },
  modalBackdrop: { flex: 1 },
  btn: {
    flexDirection: 'row', alignItems: 'center', height: 36, paddingHorizontal: 10,
    borderWidth: 1, borderColor: AdminColors.borderDefault, borderRadius: 5, backgroundColor: AdminColors.bgSurface,
  },
  val: { flex: 1, fontSize: 13, color: AdminColors.textPrimary, fontFamily: FontFamily.regular },
  placeholder: { color: AdminColors.gray350 },
  arrow: { fontSize: 9, color: AdminColors.gray350 },
  dropdown: {
    position: 'absolute',
    backgroundColor: AdminColors.bgSurface, borderWidth: 1, borderColor: AdminColors.borderDefault,
    borderRadius: 6, shadowColor: '#000', shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1, shadowRadius: 12, elevation: 8, maxHeight: 220,
  },
  optionList: { maxHeight: 220 },
  opt: { paddingHorizontal: 12, paddingVertical: 8 },
  optActive: { backgroundColor: AdminColors.blue25 },
  optText: { fontSize: 13, color: AdminColors.textPrimary, fontFamily: FontFamily.regular },
  optTextActive: { color: AdminColors.blue700, fontFamily: FontFamily.medium },
});

// ─── Employee form state & constants ─────────────────────────────────────────
// Fields match the Add/Edit Employee acceptance criteria exactly. GPID is part of
// the form but locked (read-only) in edit mode — changing it requires deactivating
// the record and creating a new one instead.
export interface EmpFormState {
  gpid: string; firstName: string; lastName: string; status: string;
  role: string; shift: string; department: string; manager: string;
  organization: string; location: string; joiningType: string;
  preferredLanguage: string; hireDate: string; newHire: boolean; peerCoach: string;
}
export const BLANK_FORM: EmpFormState = {
  gpid: '', firstName: '', lastName: '', status: 'Active',
  role: 'Picker', shift: '1st', department: 'Warehouse', manager: '',
  organization: '', location: '', joiningType: '',
  preferredLanguage: 'English', hireDate: '', newHire: false, peerCoach: '',
};
// Fallbacks only — EmployeeModal prefers live manager/department/role/organization/location
// options from redux (FieldUsers+COEUsers for manager, master data LocTypes for department,
// appData.roles for role, master data OrgTypes for organization, appData.locations for location).
const FALLBACK_MANAGERS = ['Amara Torres', 'Marcus Webb', 'Rita Alvarez'];
const FALLBACK_DEPARTMENTS = ['Warehouse'];
const FALLBACK_ROLES = ['Picker', 'Replenishment', 'Transport Load & Unload', 'Layer Claw', 'Repack', 'Sanitation', 'Chilled', 'Supervisor'];
const FALLBACK_ORGS = ['PBNA', 'FLNA', '1NA'];
const SHIFTS = ['1st', '2nd', '3rd'];
const JOINING_TYPES = ['New Hire', 'Transferred Employee'];
const PREFERRED_LANGUAGES = ['English', 'Spanish', 'Vietnamese', 'Mandarin', 'Other'];
const STATUS_OPTIONS_NEW_HIRE = ['In Onboarding'];
const STATUS_OPTIONS_TRANSFERRED = ['Active', 'On Leave'];
// Field-level sanitation per AC: trim, strip HTML tags/control chars, cap at 100 chars.
function sanitizeTextField(value: string): string {
  return value
    .replace(/<[^>]*>/g, '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1F\x7F]/g, '')
    .trim()
    .slice(0, 100);
}

// Builds the Edit Employee form state from a roster/profile MyTeamMember — reused by both
// the My Team table's own Edit Employee button and the Employee Profile page's Edit button.
export function buildEditFormFromMember(member: MyTeamMember): EmpFormState {
  const [firstName, ...lastParts] = member.name.split(' ');
  return {
    gpid: member.gpid, firstName, lastName: lastParts.join(' '), status: member.status,
    role: member.role, shift: member.shift, department: member.department || 'Warehouse',
    manager: member.manager, organization: member.org || '', location: '',
    joiningType: member.newHire ? 'New Hire' : 'Transferred Employee',
    preferredLanguage: member.preferredLanguage || 'English',
    hireDate: member.hireDate, newHire: !!member.newHire, peerCoach: member.peerCoach ?? '',
  };
}

// ─── Employee Modal (Add / Edit) ──────────────────────────────────────────────
interface EmployeeModalProps {
  visible: boolean; mode: 'add' | 'edit'; initial: EmpFormState;
  onClose: () => void; onSubmit: (form: EmpFormState) => void;
  /** GPIDs already on the roster at this location — used for the duplicate-GPID
   *  check on Add (excludes the record being edited when mode is 'edit'). */
  existingGpids?: string[];
  /** True when the employee being edited currently has an open onboarding plan
   *  (status === 'In Onboarding') — gates the "close plan" confirmation dialog. */
  hasOpenOnboardingPlan?: boolean;
}
export const EmployeeModal: React.FC<EmployeeModalProps> = ({
  visible, mode, initial, onClose, onSubmit, existingGpids = [], hasOpenOnboardingPlan = false,
}) => {
  const [form, setForm] = useState<EmpFormState>(initial);
  // The baseline form actually compared against isDirty — differs from the raw `initial`
  // prop because the mount effect below auto-fills organization/location defaults.
  const [baselineForm, setBaselineForm] = useState<EmpFormState>(initial);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [showClosePlanConfirm, setShowClosePlanConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  // GPID directory lookup (Add only) — mirrors the config module's Add Employee form:
  // once GPID reaches 8 digits, fetch and prefill name/role/manager, and lock those
  // fields until the GPID itself changes again.
  const [gpidLookupLoading, setGpidLookupLoading] = useState(false);
  const [gpidLookupError, setGpidLookupError] = useState<string | null>(null);
  const [isNameFromGpidLookup, setIsNameFromGpidLookup] = useState(false);

  const fieldUsers = useSelector((state: any) => state.appData?.FieldUsers ?? []);
  const coeUsers = useSelector((state: any) => state.appData?.COEUsers ?? []);
  const locTypes = useSelector(selectLocTypes);
  const orgTypes = useSelector(selectOrgTypes);
  const appDataRoles = useSelector((state: any) => state.appData?.roles);
  const accessibleLocations = useSelector((state: any) => state.appData?.locations ?? []);
  const selectedLocation = useSelector((state: any) => state.appData?.selectedLocation);

  // Manager dropdown mirrors the Employee Updates (configuration module) source —
  // every FieldUsers/COEUsers login for the current location, not a static list.
  const managerOptions = useMemo(() => {
    const names = [...fieldUsers, ...coeUsers]
      .map((u: any) => (u.UserEmail ? emailToDisplayName(u.UserEmail) : ''))
      .filter((name: string) => name.trim().length > 0);
    const unique = [...new Set(names)].sort((a, b) => a.localeCompare(b));
    return unique.length > 0 ? unique : FALLBACK_MANAGERS;
  }, [fieldUsers, coeUsers]);

  const departmentOptions = locTypes.length > 0 ? locTypes : FALLBACK_DEPARTMENTS;
  const orgOptions = orgTypes.length > 0 ? orgTypes : FALLBACK_ORGS;
  const statusOptions = form.joiningType === 'New Hire' ? STATUS_OPTIONS_NEW_HIRE : STATUS_OPTIONS_TRANSFERRED;

  // Location dropdown — every location the current user has access to (same roster
  // as the location-with-access response), not a static list.
  const locationOptions = useMemo(() => {
    const names = (accessibleLocations as any[])
      .map((l) => l?.name)
      .filter((name: any): name is string => typeof name === 'string' && name.trim().length > 0);
    const unique = [...new Set(names)].sort((a, b) => a.localeCompare(b));
    if (unique.length > 0) return unique;
    const current = selectedLocation?.name ?? selectedLocation?.displayText;
    return current ? [current] : [];
  }, [accessibleLocations, selectedLocation]);

  // Role dropdown mirrors the Employee Updates (configuration module) Job Description
  // source — appData.roles (ROLE_NAME/ROLE_ID), not a static list.
  const roleOptions = useMemo(() => {
    if (!Array.isArray(appDataRoles)) return FALLBACK_ROLES;
    const names = appDataRoles
      .filter((role: any) => role.ROLE_NAME && role.ROLE_ID)
      .map((role: any) => role.ROLE_NAME as string)
      .sort((a: string, b: string) => a.localeCompare(b));
    return names.length > 0 ? names : FALLBACK_ROLES;
  }, [appDataRoles]);

  React.useEffect(() => {
    if (!visible) return;
    // Add and Edit Employee are both locked to the location the user currently has
    // selected — Organization/Department/Location are pre-filled from it and read-only.
    const defaulted: EmpFormState = {
      ...initial,
      organization: selectedLocation?.organizationType || initial.organization || orgOptions[0] || '',
      department: selectedLocation?.locationType || initial.department || departmentOptions[0] || '',
      location: selectedLocation?.name || selectedLocation?.displayText || initial.location || locationOptions[0] || '',
    };
    setForm(defaulted);
    setBaselineForm(defaulted);
    setActiveField(null);
    setFormError(null);
    setShowClosePlanConfirm(false);
    setShowDiscardConfirm(false);
  }, [initial, visible, mode]);

  const set = (k: keyof EmpFormState, v: string) => setForm(f => ({ ...f, [k]: v }));
  const isAdd = mode === 'add';
  const isDirty = JSON.stringify(form) !== JSON.stringify(baselineForm);

  const performGpidLookup = async (gpid: string) => {
    setGpidLookupLoading(true);
    try {
      const result = await lookupEmployeeByGpid(gpid);
      if (result) {
        setForm(f => ({
          ...f,
          firstName: result.first_name ?? f.firstName,
          lastName: result.last_name ?? f.lastName,
          role: result.job_description ?? f.role,
          manager: result.manager ?? f.manager,
        }));
        setIsNameFromGpidLookup(true);
      }
    } catch (err) {
      console.warn('GPID lookup failed:', err);
    } finally {
      setGpidLookupLoading(false);
    }
  };

  const handleGpidChange = (raw: string) => {
    const cleaned = raw.replace(/[^0-9]/g, '').slice(0, 8);
    setGpidLookupError(null);
    // Changing GPID after a lookup-prefilled name clears the lock so the user can
    // correct it manually or trigger a fresh lookup for the new GPID.
    if (isNameFromGpidLookup && cleaned !== form.gpid) {
      setForm(f => ({ ...f, gpid: cleaned, firstName: '', lastName: '' }));
      setIsNameFromGpidLookup(false);
    } else {
      set('gpid', cleaned);
    }
    if (cleaned.length === 8) {
      performGpidLookup(cleaned);
    }
  };

  // Cancel goes straight through when nothing changed; otherwise confirm first
  // so an accidental tap can't silently drop in-progress edits.
  const handleCancelPress = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  // Auto-dismiss the "no employee found" GPID lookup message after 5 seconds.
  React.useEffect(() => {
    if (!gpidLookupError) return;
    const timer = setTimeout(() => setGpidLookupError(null), 5000);
    return () => clearTimeout(timer);
  }, [gpidLookupError]);

  const noteText = !form.joiningType
    ? 'Choose a joining type to determine whether this employee enters the onboarding program.'
    : form.joiningType === 'New Hire'
    ? 'Adding this PBNA Distribution new hire creates the 90-day onboarding plan, certification work, training, and milestone reviews. Status is fixed to In Onboarding.'
    : 'A transferred employee is added as Active or On Leave. No onboarding plan is created.';

  // Applies the Joining Type selection: New Hire turns newHire on and pre-selects
  // Status = In Onboarding; Transferred Employee turns newHire off and pre-selects
  // Status = Active.
  const applyJoiningType = (value: string) => {
    setForm(f => ({
      ...f,
      joiningType: value,
      newHire: value === 'New Hire',
      status: value === 'New Hire' ? 'In Onboarding' : 'Active',
    }));
  };

  // Switching away from New Hire on an employee with an open plan is gated behind
  // the "This will close the onboarding plan..." confirmation.
  const handleJoiningTypeChange = (value: string) => {
    const turningOff = !isAdd && form.joiningType === 'New Hire' && value !== 'New Hire';
    if (turningOff && hasOpenOnboardingPlan) {
      setShowClosePlanConfirm(true);
      return;
    }
    applyJoiningType(value);
  };

  const validate = (): string | null => {
    const firstName = sanitizeTextField(form.firstName);
    const lastName = sanitizeTextField(form.lastName);
    const peerCoach = sanitizeTextField(form.peerCoach);

    if (!firstName) return 'First Name is required.';
    if (!lastName) return 'Last Name is required.';
    if (!/^\d{8}$/.test(form.gpid.trim())) return 'GPID must be eight digits.';
    if (!form.organization.trim()) return 'Organization is required.';
    if (!form.location.trim()) return 'Location is required.';
    if (!form.role.trim()) return 'Role is required.';
    if (!form.shift.trim()) return 'Shift is required.';
    if (!form.department.trim()) return 'Department is required.';
    if (!form.manager.trim()) return 'Manager is required.';
    if (!form.hireDate.trim()) return 'Hire Date is required.';
    if (isAdd && !form.joiningType) return 'Select a joining type.';
    if (form.newHire && !peerCoach) return 'Peer Coach is required when New Hire is on.';
    if (isAdd && existingGpids.includes(form.gpid.trim())) {
      return `An employee with GPID ${form.gpid.trim()} already exists at this location.`;
    }
    return null;
  };

  const canSubmit = validate() === null;

  const handleSave = () => {
    const err = validate();
    if (err) { setFormError(err); return; }
    onSubmit({
      ...form,
      firstName: sanitizeTextField(form.firstName),
      lastName: sanitizeTextField(form.lastName),
      peerCoach: sanitizeTextField(form.peerCoach),
      gpid: form.gpid.trim(),
    });
    onClose();
  };

  const pair = (a: React.ReactNode, b: React.ReactNode) => (
    <View style={mStyles.row}><View style={mStyles.half}>{a}</View><View style={mStyles.half}>{b}</View></View>
  );

  return (
    <AdminModal
      visible={visible}
      title={isAdd ? 'Add Employee' : 'Edit Employee'}
      onClose={onClose}
      width={620}
      footer={
        <>
          <AdminButton label="Cancel" variant="secondary" onPress={handleCancelPress} />
          <AdminButton
            label={isAdd ? 'Add Employee' : 'Update Employee'}
            variant="primary"
            disabled={!canSubmit}
            onPress={handleSave}
          />
        </>
      }
    >
      <ScrollView contentContainerStyle={mStyles.body} nestedScrollEnabled>
        {pair(
          <AdminFormField label="Organization" required>
            <AdminTextInput value={form.organization} editable={false} readOnly />
          </AdminFormField>,
          <AdminFormField label="Department" required>
            <AdminTextInput value={form.department} editable={false} readOnly />
          </AdminFormField>,
        )}
        {pair(
          <AdminFormField label="Location" required>
            <AdminTextInput value={form.location} editable={false} readOnly />
          </AdminFormField>,
          <AdminFormField label="Joining Type" required={isAdd}>
            <InlinePicker
              fieldId="joiningType"
              value={form.joiningType}
              options={JOINING_TYPES}
              onChange={handleJoiningTypeChange}
              placeholder="Choose joining type"
              activeField={activeField}
              onOpenChange={setActiveField}
            />
          </AdminFormField>,
        )}
        {pair(
          <AdminFormField
            label="GPID"
            required
            hint={isAdd && gpidLookupLoading ? 'Looking up employee…' : (isAdd && gpidLookupError) || undefined}
          >
            <AdminTextInput
              value={form.gpid}
              onChangeText={isAdd ? handleGpidChange : undefined}
              placeholder="8-digit GPID"
              keyboardType="numeric"
              maxLength={8}
              editable={isAdd}
              readOnly={!isAdd}
            />
          </AdminFormField>,
          <AdminFormField label="First Name" required>
            <AdminTextInput
              value={form.firstName}
              onChangeText={v => set('firstName', v)}
              maxLength={100}
              editable={!isNameFromGpidLookup}
              readOnly={isNameFromGpidLookup}
            />
          </AdminFormField>,
        )}
        {pair(
          <AdminFormField label="Last Name" required>
            <AdminTextInput
              value={form.lastName}
              onChangeText={v => set('lastName', v)}
              maxLength={100}
              editable={!isNameFromGpidLookup}
              readOnly={isNameFromGpidLookup}
            />
          </AdminFormField>,
          <AdminFormField label="Status">
            <InlinePicker
              fieldId="status"
              value={form.status}
              options={statusOptions}
              onChange={v => set('status', v)}
              activeField={activeField}
              onOpenChange={setActiveField}
            />
          </AdminFormField>,
        )}
        {pair(
          <AdminFormField label="Manager" required>
            <InlinePicker fieldId="manager" value={form.manager} options={managerOptions} onChange={v => set('manager', v)} activeField={activeField} onOpenChange={setActiveField} />
          </AdminFormField>,
          <AdminFormField label="Role" required>
            <InlinePicker fieldId="role" value={form.role} options={roleOptions} onChange={v => set('role', v)} activeField={activeField} onOpenChange={setActiveField} />
          </AdminFormField>,
        )}
        {pair(
          <AdminFormField label="Hire Date" required>
            <LhDatePicker
              value={form.hireDate ? new Date(form.hireDate) : null}
              onChange={(date) => set('hireDate', date ? date.toISOString().slice(0, 10) : '')}
              placeholder="Select hire date"
            />
          </AdminFormField>,
          <AdminFormField label="Shift" required>
            <InlinePicker fieldId="shift" value={form.shift} options={SHIFTS} onChange={v => set('shift', v)} activeField={activeField} onOpenChange={setActiveField} />
          </AdminFormField>,
        )}
        <AdminFormField label="Preferred Language">
          <InlinePicker fieldId="preferredLanguage" value={form.preferredLanguage} options={PREFERRED_LANGUAGES} onChange={v => set('preferredLanguage', v)} activeField={activeField} onOpenChange={setActiveField} />
        </AdminFormField>
        {form.newHire && (
          <AdminFormField label="Peer Coach" required>
            <AdminTextInput value={form.peerCoach} onChangeText={v => set('peerCoach', v)} maxLength={100} />
          </AdminFormField>
        )}
        {!isAdd && (
          <AdminFormField label="Expected Graduation Date">
            <Text style={mStyles.readOnlyValue}>
              {initial.newHire ? 'Computed by the server once saved' : '—'}
            </Text>
          </AdminFormField>
        )}
        {!!formError && <Text style={mStyles.errorText}>{formError}</Text>}
        <Text style={mStyles.note}>{noteText}</Text>
      </ScrollView>

      {/* "This will close the onboarding plan..." confirmation for New Hire off */}
      <Modal visible={showClosePlanConfirm} transparent animationType="fade" onRequestClose={() => setShowClosePlanConfirm(false)}>
        <View style={mStyles.confirmBackdrop}>
          <View style={mStyles.confirmCard}>
            <Text style={mStyles.confirmText}>
              This will close the onboarding plan and its open items. Continue?
            </Text>
            <View style={mStyles.confirmButtons}>
              <AdminButton label="Cancel" variant="secondary" onPress={() => setShowClosePlanConfirm(false)} />
              <AdminButton
                label="Close plan"
                variant="primary"
                onPress={() => { applyJoiningType('Transferred Employee'); setShowClosePlanConfirm(false); }}
              />
            </View>
          </View>
        </View>
      </Modal>

      {/* "Discard unsaved changes?..." confirmation for Cancel with dirty form */}
      <Modal visible={showDiscardConfirm} transparent animationType="fade" onRequestClose={() => setShowDiscardConfirm(false)}>
        <View style={mStyles.confirmBackdrop}>
          <View style={mStyles.confirmCard}>
            <Text style={mStyles.confirmText}>
              Discard unsaved changes? This employee record will not be updated.
            </Text>
            <View style={mStyles.confirmButtons}>
              <AdminButton label="Keep editing" variant="secondary" onPress={() => setShowDiscardConfirm(false)} />
              <AdminButton
                label="Discard"
                variant="primary"
                onPress={() => { setShowDiscardConfirm(false); onClose(); }}
              />
            </View>
          </View>
        </View>
      </Modal>
    </AdminModal>
  );
};
const mStyles = StyleSheet.create({
  body: { paddingBottom: 4 },
  row: { flexDirection: 'row', gap: 12 },
  half: { flex: 1 },
  note: { fontSize: 11, fontFamily: FontFamily.regular, color: AdminColors.textMuted, marginTop: 4, lineHeight: 16 },
  errorText: { fontSize: 12, fontFamily: FontFamily.medium, color: AdminColors.red500, marginTop: 8 },
  readOnlyValue: { fontSize: 13, fontFamily: FontFamily.regular, color: AdminColors.textMuted, paddingVertical: 8 },
  toggleTrack: {
    width: 44, height: 24, borderRadius: 12, backgroundColor: AdminColors.gray200,
    padding: 2, justifyContent: 'center',
  },
  toggleTrackOn: { backgroundColor: AdminColors.blue700 },
  toggleThumb: {
    width: 20, height: 20, borderRadius: 10, backgroundColor: '#fff',
  },
  toggleThumbOn: { alignSelf: 'flex-end' },
  confirmBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', alignItems: 'center', justifyContent: 'center' },
  confirmCard: { width: 360, backgroundColor: AdminColors.bgSurface, borderRadius: 10, padding: 20, gap: 16 },
  confirmText: { fontSize: 14, fontFamily: FontFamily.regular, color: AdminColors.textPrimary, lineHeight: 20 },
  confirmButtons: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10 },
});

// ─── Group By pill bar ────────────────────────────────────────────────────────
interface GroupByBarProps { value: GroupBy; onChange: (v: GroupBy) => void; }
const GroupByBar: React.FC<GroupByBarProps> = ({ value, onChange }) => {
  const opts: { id: GroupBy; label: string }[] = [
    { id: 'none', label: 'Flat list' },
    { id: 'role', label: 'Role' },
    { id: 'manager', label: 'Manager' },
  ];
  return (
    <View style={gbStyles.row}>
      <Text style={gbStyles.label}>Group by</Text>
      <View style={gbStyles.track}>
        {opts.map(opt => (
          <TouchableOpacity
            key={opt.id}
            style={[gbStyles.pill, value === opt.id && gbStyles.pillActive]}
            onPress={() => onChange(opt.id)}
            activeOpacity={0.75}
          >
            <Text style={[gbStyles.pillText, value === opt.id && gbStyles.pillTextActive]}>{opt.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
};
const gbStyles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  label: {
    fontSize: 12, lineHeight: 18, fontWeight: '700', color: AdminColors.textSecondary,
    fontFamily: FontFamily.bold, letterSpacing: 0.5, textTransform: 'uppercase',
  },
  // .ov-toggle: tinted track, 8px radius, 2px padding; active segment is a white raised chip.
  track: { flexDirection: 'row', gap: 2, padding: 2, borderRadius: 8, backgroundColor: AdminColors.bgHover },
  pill: { paddingHorizontal: 14, minHeight: 30, borderRadius: 6, alignItems: 'center', justifyContent: 'center' },
  pillActive: {
    backgroundColor: AdminColors.bgSurface,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.12, shadowRadius: 2, elevation: 1,
  },
  pillText: { fontSize: 13, lineHeight: 18, fontWeight: '700', fontFamily: FontFamily.bold, color: AdminColors.textSecondary },
  pillTextActive: { color: AdminColors.blue700 },
});

// ─── Column definitions ──────────────────────────────────────────────────────
// Widths are chosen to match the HTML reference: label text (11px caps) + sort
// icons (10px) + filter icon (20px) + 2×10px horizontal cell padding.
interface ColDef { key: string; label: string; width: number; numeric?: boolean; }

const FIXED_COLS: ColDef[] = [
  { key: 'name',           label: 'Employee Name',  width: 186 },
  { key: 'gpid',           label: 'GPID',           width: 116 },
  { key: 'manager',        label: 'Manager',        width: 150 },
  { key: 'role',           label: 'Role',           width: 156 },
  { key: 'shift',          label: 'Shift',          width: 100 },
  { key: 'department',     label: 'Department',     width: 140 },
  { key: 'org',            label: 'Org',            width: 84 },
  { key: 'hireDate',       label: 'Hire Date',      width: 132 },
  { key: 'seniority',      label: 'Seniority',      width: 120 },
  { key: 'seniorityOrder', label: 'Seniority Order',width: 160, numeric: true },
  { key: 'status',         label: 'Status',         width: 132 },
];
// Metric cols — right-aligned numeric; activity cols at the end
const ACTIVITY_COLS: ColDef[] = [
  { key: 'lastPulseCheck',  label: 'Pulse Check',  width: 132 },
  { key: 'lastObservation', label: 'Observation',  width: 132 },
];
// Metric column width: label "Cases Picked" is the longest at ~10 chars caps
const METRIC_COL_W = 126;

const RADIO_W = 36;
const AVATAR_W = 32;
const ROW_H = 56;

// Total table content width — used to give full-width stretch to group headers,
// the empty-state row, and the tfoot row inside the horizontal scroll area.
const TABLE_TOTAL_W =
  RADIO_W +
  FIXED_COLS.reduce((s, c) => s + c.width, 0) +
  METRIC_COLS.length * METRIC_COL_W +
  ACTIVITY_COLS.reduce((s, c) => s + c.width, 0);

// ─── Main Component ───────────────────────────────────────────────────────────
interface PeopleHubMyTeamProps {
  /** Pre-fills search and selects the matching row — set when navigating here from My Queue. */
  focusEmployeeName?: string | null;
  /** Opens the Employee Profile screen for this roster row (People Hub > My Team > {name}). */
  onOpenEmployeeProfile?: (member: MyTeamMember) => void;
}

const PeopleHubMyTeam: React.FC<PeopleHubMyTeamProps> = ({ focusEmployeeName, onOpenEmployeeProfile }) => {
  const [search, setSearch]               = useState('');
  const [sortKey, setSortKey]             = useState<string>('name');
  const [sortDir, setSortDir]             = useState<SortDir>('asc');
  const [openFilterCol, setOpenFilterCol] = useState<string | null>(null);
  const [colFilters, setColFilters]       = useState<Record<string, Set<string>>>({});
  const [groupBy, setGroupBy]             = useState<GroupBy>('none');
  const [selectedId, setSelectedId]       = useState<string | null>(null);
  const [modalMode, setModalMode]         = useState<'add' | 'edit' | null>(null);
  const [data, setData]                   = useState<MyTeamMember[]>([]);
  const [isLoading, setIsLoading]         = useState(false);

  const selectedLocation = useSelector((state: any) => state.appData?.selectedLocation);
  const accessibleLocations = useSelector((state: any) => state.appData?.locations ?? []);
  const locationName: string | undefined = selectedLocation?.name ?? selectedLocation?.displayText;

  // Resolves a location's id from appData.locations by name — so Add/Edit Employee
  // payloads can carry locationId alongside locationName.
  const resolveLocationId = useCallback((name: string | undefined): number | string | undefined => {
    if (!name) return undefined;
    const match = (accessibleLocations as any[]).find(
      (l) => l?.name === name || l?.displayText === name,
    );
    return match?.locationId ?? match?.id ?? match?.LocationId ?? undefined;
  }, [accessibleLocations]);

  const loadTeam = useCallback(async () => {
    if (!locationName) return;
    setIsLoading(true);
    try {
      const res = await fetchMyTeam(locationName);
      const rows: Record<string, any>[] = res?.teamMembers ?? [];
      setData(rows.map(mapRosterRow));
    } catch (err) {
      console.warn('Failed to load My Team roster, keeping existing data', err);
    } finally {
      setIsLoading(false);
    }
  }, [locationName]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  useEffect(() => {
    if (!focusEmployeeName) return;
    setSearch(focusEmployeeName);
    const match = data.find((m) => m.name === focusEmployeeName);
    if (match) setSelectedId(match.id);
  }, [focusEmployeeName, data]);

  const handleSort = (k: string) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('asc'); }
  };

  const handleFilterToggle = (k: string) =>
    setOpenFilterCol(prev => prev === k ? null : k);

  // All column keys (fixed + metrics + activity) for filter options
  const ALL_COLS: ColDef[] = [
    ...FIXED_COLS,
    ...METRIC_COLS.map(m => ({ key: m.key, label: m.label, width: 80, numeric: true })),
    ...ACTIVITY_COLS,
  ];

  // Per-column unique string options
  const filterOptions = useMemo(() => {
    const opts: Record<string, string[]> = {};
    ALL_COLS.forEach(col => {
      const vals = [...new Set(data.map(r => {
        const v = r[col.key as keyof MyTeamMember];
        return v === null || v === undefined ? '—' : String(v);
      }))].sort();
      opts[col.key] = vals;
    });
    return opts;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Filtered + sorted rows
  const visible = useMemo(() => {
    let rows = data.filter(r => {
      const q = search.toLowerCase().trim();
      if (q && !r.name.toLowerCase().includes(q) && !r.gpid.includes(q) &&
          !r.role.toLowerCase().includes(q) && !r.manager.toLowerCase().includes(q)) return false;

      for (const [k, selected] of Object.entries(colFilters)) {
        // Empty set = no filter active (show all). Non-empty = show only rows whose value is in the set.
        if (!selected || selected.size === 0) continue;
        const raw = r[k as keyof MyTeamMember];
        const cellVal = raw === null || raw === undefined ? '—' : String(raw);
        if (!selected.has(cellVal)) return false;
      }
      return true;
    });

    rows.sort((a, b) => {
      const aRaw = a[sortKey as keyof MyTeamMember];
      const bRaw = b[sortKey as keyof MyTeamMember];
      // Numeric sort for numbers, string sort for rest
      if (typeof aRaw === 'number' && typeof bRaw === 'number') {
        return sortDir === 'asc' ? aRaw - bRaw : bRaw - aRaw;
      }
      const av = aRaw === null || aRaw === undefined ? '' : String(aRaw).toLowerCase();
      const bv = bRaw === null || bRaw === undefined ? '' : String(bRaw).toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return rows;
  }, [data, search, colFilters, sortKey, sortDir]);

  // Grouped rows
  const groups = useMemo(() => {
    if (groupBy === 'none') return [{ key: '', rows: visible }];
    const map = new Map<string, MyTeamMember[]>();
    visible.forEach(r => {
      const gk = groupBy === 'role' ? r.role : r.manager;
      if (!map.has(gk)) map.set(gk, []);
      map.get(gk)!.push(r);
    });
    return [...map.entries()].map(([key, rows]) => ({ key, rows }));
  }, [visible, groupBy]);

  const selectedMember = data.find(m => m.id === selectedId) ?? null;

  const initialForm = useMemo<EmpFormState>(() => {
    if (!selectedMember || modalMode !== 'edit') return BLANK_FORM;
    return buildEditFormFromMember(selectedMember);
  }, [selectedMember, modalMode]);

  // Duplicate-GPID check excludes the record currently being edited.
  const existingGpids = useMemo(
    () => data.filter(m => m.id !== selectedMember?.id).map(m => m.gpid),
    [data, selectedMember],
  );

  const toast = useToast();

  const handleSubmit = async (form: EmpFormState) => {
    const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`;
    // Optimistic local update so the table responds immediately.
    if (modalMode === 'add') {
      const newMember: MyTeamMember = {
        id: String(Date.now()), name: fullName, gpid: form.gpid.trim(),
        manager: form.manager, role: form.role, shift: form.shift,
        department: form.department, org: form.organization, hireDate: form.hireDate,
        seniority: '0Y 0D', seniorityOrder: data.length + 1,
        status: form.status as TeamMemberRosterStatus,
        peerCoach: form.peerCoach, preferredLanguage: form.preferredLanguage, newHire: form.newHire,
        expectedGraduationDate: null,
        cases: null, gapTime: null, cph: null, dps: null, bpa: null, ku: null,
        lastPulseCheck: null, lastObservation: null,
      };
      setData(d => [...d, newMember]);
    } else if (modalMode === 'edit' && selectedMember) {
      setData(d => d.map(m => m.id === selectedMember.id ? {
        ...m, name: fullName,
        manager: form.manager, role: form.role, shift: form.shift,
        department: form.department, org: form.organization, hireDate: form.hireDate,
        status: form.status as TeamMemberRosterStatus,
        peerCoach: form.peerCoach, preferredLanguage: form.preferredLanguage, newHire: form.newHire,
      } : m));
    }

    try {
      if (modalMode === 'add') {
        const resolvedLocationName = form.location || locationName;
        await addEmployee({
          gpid: form.gpid.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          role: form.role,
          shift: form.shift,
          department: form.department,
          organization: form.organization,
          manager: form.manager,
          locationName: resolvedLocationName,
          locationId: resolveLocationId(resolvedLocationName),
          hireDate: form.hireDate || undefined,
          status: form.status,
          peerCoach: form.newHire ? form.peerCoach : undefined,
          preferredLanguage: form.preferredLanguage,
          newHire: form.newHire,
        });
        toast.show('Employee added.', 'success');
      } else if (modalMode === 'edit' && selectedMember) {
        await updateEmployee(selectedMember.gpid, {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          role: form.role,
          shift: form.shift,
          department: form.department,
          organization: form.organization,
          locationName: form.location,
          locationId: resolveLocationId(form.location),
          manager: form.manager,
          status: form.status,
          peerCoach: form.newHire ? form.peerCoach : undefined,
          preferredLanguage: form.preferredLanguage,
          newHire: form.newHire,
          hireDate: form.hireDate || undefined,
        });
        toast.show('Employee updated.', 'success');
      }
      await loadTeam();
    } catch (err) {
      console.warn('Failed to save employee to backend', err);
      toast.show('Failed to save employee. Please try again.', 'error');
    }
  };

  // Render a cell value for a fixed column
  const renderCell = (member: MyTeamMember, key: string) => {
    if (key === 'name') {
      return (
        <Pressable onPress={() => onOpenEmployeeProfile?.(member)} hitSlop={4}>
          <Text style={styles.nameCell} numberOfLines={1}>{member.name}</Text>
        </Pressable>
      );
    }
    if (key === 'status') {
      return <StatusBadge status={member.status} />;
    }
    const raw = member[key as keyof MyTeamMember];
    const display = key === 'hireDate' && typeof raw === 'string' ? formatLongDate(raw) : raw;
    return (
      <Text style={styles.cellText} numberOfLines={1}>
        {display === null || display === undefined || display === '' ? '—' : String(display)}
      </Text>
    );
  };

  return (
    <Pressable style={styles.container} onPress={() => setOpenFilterCol(null)}>
      {/* ── Subheader ── */}
      <View style={styles.subheader}>
        <View style={styles.searchWrap}>
          <MaterialIcons name="search" size={16} color={AdminColors.gray350} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search employee, role, manager, or GPID"
            value={search}
            onChangeText={setSearch}
            placeholderTextColor={AdminColors.gray350}
          />
        </View>
        <View style={styles.subheaderRight}>
          <Text style={styles.countText}>{visible.length} of {data.length}</Text>
          {isLoading && <ActivityIndicator size="small" color={AdminColors.blue700} />}
          <AdminButton
            label="Edit Employee"
            variant="secondary"
            small
            disabled={!selectedId}
            onPress={() => { if (selectedId) setModalMode('edit'); }}
          />
          <AdminButton
            label="+ Add Employee"
            variant="primary"
            small
            onPress={() => { setSelectedId(null); setModalMode('add'); }}
          />
        </View>
      </View>

      {/* ── Group By bar ── */}
      <GroupByBar value={groupBy} onChange={setGroupBy} />

      {/* ── Table box: 1px border, 10px radius (mockup .people-table-wrap) ── */}
      <View style={styles.tableBox}>
      <ScrollView horizontal style={styles.tableScroll}>
        <View style={styles.tableWrap}>

          {/* ── Header row ── */}
          <View style={styles.theadRow}>
            {/* Radio */}
            <View style={{ width: RADIO_W }} />

            {/* Fixed columns */}
            {FIXED_COLS.map(col => (
              <View key={col.key} style={[styles.thCell, { width: col.width }, col.numeric && styles.thCellRight]}>
                <SortableCol
                  label={col.label} colKey={col.key}
                  sortKey={sortKey} sortDir={sortDir} onSort={handleSort}
                  filterOptions={filterOptions[col.key] ?? []}
                  filterSelected={colFilters[col.key] ?? new Set()}
                  onFilterChange={next => setColFilters(f => ({ ...f, [col.key]: next }))}
                  openFilterCol={openFilterCol} onFilterToggle={handleFilterToggle}
                  align={col.numeric ? 'right' : 'left'}
                />
              </View>
            ))}

            {/* Metric columns */}
            {METRIC_COLS.map(col => (
              <View key={col.key} style={[styles.thCell, { width: METRIC_COL_W }, styles.thCellRight]}>
                <SortableCol
                  label={col.label} colKey={col.key}
                  sortKey={sortKey} sortDir={sortDir} onSort={handleSort}
                  filterOptions={filterOptions[col.key] ?? []}
                  filterSelected={colFilters[col.key] ?? new Set()}
                  onFilterChange={next => setColFilters(f => ({ ...f, [col.key]: next }))}
                  openFilterCol={openFilterCol} onFilterToggle={handleFilterToggle}
                  align="right"
                />
              </View>
            ))}

            {/* Activity columns */}
            {ACTIVITY_COLS.map(col => (
              <View key={col.key} style={[styles.thCell, { width: col.width }]}>
                <SortableCol
                  label={col.label} colKey={col.key}
                  sortKey={sortKey} sortDir={sortDir} onSort={handleSort}
                  filterOptions={filterOptions[col.key] ?? []}
                  filterSelected={colFilters[col.key] ?? new Set()}
                  onFilterChange={next => setColFilters(f => ({ ...f, [col.key]: next }))}
                  openFilterCol={openFilterCol} onFilterToggle={handleFilterToggle}
                />
              </View>
            ))}
          </View>

          {/* ── Body ── */}
          {/* Use a plain View — vertical scrolling is handled by the parent tab container.
              A ScrollView (even with scrollEnabled=false) reserves viewport height and
              creates an empty gap below the last row. */}
          <View>
            {groups.map(group => (
              <View key={group.key || 'all'}>
                {!!group.key && (
                  <View style={styles.groupHeader}>
                    <Text style={styles.groupHeaderText}>
                      {group.key}{' '}
                      <Text style={styles.groupHeaderCount}>({group.rows.length})</Text>
                    </Text>
                  </View>
                )}
                {group.rows.map(member => {
                  const isSelected = member.id === selectedId;
                  return (
                    <Pressable
                      key={member.id}
                      style={({ pressed, hovered }: any) => [
                        styles.row,
                        isSelected ? styles.rowSelected : (hovered || pressed) && styles.rowHover,
                      ]}
                    >
                      {/* Radio — the only thing that toggles selection */}
                      <Pressable
                        style={[styles.cell, { width: RADIO_W, alignItems: 'center' }]}
                        onPress={() => setSelectedId(member.id === selectedId ? null : member.id)}
                        hitSlop={8}
                      >
                        <View style={[styles.radioOuter, isSelected && styles.radioOuterSelected]}>
                          {isSelected && <View style={styles.radioDot} />}
                        </View>
                      </Pressable>
                      {/* Fixed columns */}
                      {FIXED_COLS.map(col => (
                        <View key={col.key} style={[styles.cell, { width: col.width }, col.numeric && styles.cellRight]}>
                          {renderCell(member, col.key)}
                        </View>
                      ))}
                      {/* Metric columns — cell gets red-50 background when below target */}
                      {METRIC_COLS.map(col => {
                        const val = member[col.key] as number | null;
                        const bad = isMetricBad(col, val);
                        return (
                          <View
                            key={col.key}
                            style={[styles.cell, { width: METRIC_COL_W }, styles.cellRight, bad && styles.cellBadBg]}
                          >
                            <Text style={[styles.cellText, styles.cellTextRight, bad && styles.cellBadText]} numberOfLines={1}>
                              {val === null ? '—' : val.toLocaleString('en-US', { maximumFractionDigits: col.decimals })}
                            </Text>
                          </View>
                        );
                      })}
                      {/* Activity columns */}
                      {ACTIVITY_COLS.map(col => {
                        const val = member[col.key as keyof MyTeamMember] as string | null;
                        return (
                          <View key={col.key} style={[styles.cell, { width: col.width }]}>
                            <Text style={[styles.cellText, !val && styles.cellMuted]} numberOfLines={1}>
                              {val ?? '—'}
                            </Text>
                          </View>
                        );
                      })}
                    </Pressable>
                  );
                })}
              </View>
            ))}
            {visible.length === 0 && (
              <View style={styles.emptyState}>
                {isLoading ? (
                  <>
                    <ActivityIndicator size="small" color={AdminColors.blue700} />
                    <Text style={styles.emptyText}>Loading team roster…</Text>
                  </>
                ) : (
                  <Text style={styles.emptyText}>No employees match. Reset the filters or clear the search.</Text>
                )}
              </View>
            )}

            {/* ── Team Average footer (mirrors HTML <tfoot>) ── */}
            {visible.length > 0 && (() => {
              // Compute per-metric average across all visible rows (null values excluded)
              const metricAverages = METRIC_COLS.map(col => {
                const vals = visible
                  .map(r => r[col.key] as number | null)
                  .filter((v): v is number => v !== null);
                return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
              });
              return (
                <View style={styles.tfootRow}>
                  {/* Label cell spans radio + avatar + all fixed cols */}
                  <View style={styles.tfootLabel}>
                    <Text style={styles.tfootLabelText}>
                      Team Average ({visible.length} associate{visible.length !== 1 ? 's' : ''})
                    </Text>
                  </View>
                  {/* Metric averages */}
                  {METRIC_COLS.map((col, i) => (
                    <View key={col.key} style={[styles.tfootCell, { width: METRIC_COL_W }]}>
                      <Text style={styles.tfootValue} numberOfLines={1}>
                        {metricAverages[i] === null
                          ? 'N/A'
                          : metricAverages[i]!.toLocaleString('en-US', { maximumFractionDigits: col.decimals })}
                      </Text>
                    </View>
                  ))}
                  {/* Spacer for activity cols */}
                  {ACTIVITY_COLS.map(col => (
                    <View key={col.key} style={{ width: col.width }} />
                  ))}
                </View>
              );
            })()}
          </View>
        </View>
      </ScrollView>
      </View>

      {/* ── Modals ── */}
      {modalMode !== null && (
        <EmployeeModal
          visible={true} mode={modalMode} initial={initialForm}
          existingGpids={existingGpids}
          hasOpenOnboardingPlan={selectedMember?.status === 'In Onboarding'}
          onClose={() => setModalMode(null)} onSubmit={handleSubmit}
        />
      )}
    </Pressable>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: AdminColors.bgPage, padding: 16, gap: 12 },

  subheader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' },
  searchWrap: {
    flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8,
    borderWidth: 1, borderColor: AdminColors.borderDefault, borderRadius: 6,
    paddingHorizontal: 10, height: 36, backgroundColor: AdminColors.bgSurface, minWidth: 220,
  },
  searchInput: {
    flex: 1, fontSize: 14, color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular, height: 34, padding: 0,
  },
  subheaderRight: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  // Count tag: gray fill, 4px radius (mockup <Tag>).
  countText: {
    fontSize: 12, lineHeight: 18, fontFamily: FontFamily.regular, color: AdminColors.textPrimary,
    paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4, backgroundColor: AdminColors.gray25,
  },

  // .people-table-wrap: 1px border, 10px radius, white surface.
  tableBox: {
    flexShrink: 1, backgroundColor: AdminColors.bgSurface,
    borderWidth: 1, borderColor: AdminColors.gray25, borderRadius: 10, overflow: 'hidden',
  },
  // tableScroll: flexShrink: 1 — shrinks to content height (no empty gap below last row)
  // while still allowing the horizontal ScrollView to work normally.
  tableScroll: { flexShrink: 1 },
  // minWidth: TABLE_TOTAL_W ensures group-header / empty-state / tfoot rows always
  // stretch to the full table width, eliminating the right-side gap.
  tableWrap: { backgroundColor: AdminColors.bgSurface, minWidth: TABLE_TOTAL_W },

  // thead tr: 2px bottom rule in --nav-border-table-header.
  theadRow: {
    flexDirection: 'row', alignItems: 'center',
    borderBottomWidth: 2, borderBottomColor: AdminColors.borderTable,
    backgroundColor: AdminColors.bgSurface,
  },
  // Team view: th/td padding 8px 10px.
  thCell: { paddingHorizontal: 10, paddingVertical: 8, zIndex: 10 },
  thCellRight: { alignItems: 'flex-end' },

  row: {
    flexDirection: 'row', alignItems: 'center', height: ROW_H,
    borderBottomWidth: 1, borderBottomColor: AdminColors.gray25,
  },
  rowSelected: { backgroundColor: AdminColors.blue25 },
  cell: { paddingHorizontal: 10, paddingVertical: 8, justifyContent: 'center' },
  cellRight: { alignItems: 'flex-end' },

  nameCell: { fontSize: 14, lineHeight: 20, fontFamily: FontFamily.bold, fontWeight: '700', color: AdminColors.textLink },
  cellText: { fontSize: 14, lineHeight: 20, fontFamily: FontFamily.regular, color: AdminColors.textPrimary },
  cellTextRight: { textAlign: 'right' },
  // metric-bad: full cell gets red-50 bg (#fbe6e9), text gets red-500 bold — matches HTML .metric-bad
  cellBadBg: { backgroundColor: '#fbe6e9' },
  cellBadText: { color: AdminColors.red500, fontFamily: FontFamily.bold, fontWeight: '700' },
  cellMuted: { color: AdminColors.textMuted },
  // Row hover — matches HTML --nav-bg-hover
  rowHover: { backgroundColor: AdminColors.bgHover },

  radioOuter: {
    width: 16, height: 16, borderRadius: 8,
    borderWidth: 1.5, borderColor: AdminColors.gray200,
    alignItems: 'center', justifyContent: 'center',
  },
  radioOuterSelected: { borderColor: AdminColors.blue700 },
  radioDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: AdminColors.blue700 },

  groupHeader: {
    backgroundColor: AdminColors.nearWhite, paddingHorizontal: 16, paddingVertical: 6,
    borderBottomWidth: 1, borderBottomColor: AdminColors.gray25,
  },
  groupHeaderText: { fontSize: 12, fontWeight: '700', fontFamily: FontFamily.bold, color: AdminColors.textPrimary },
  groupHeaderCount: { fontWeight: '400', fontFamily: FontFamily.regular, color: AdminColors.textMuted },

  emptyState: { alignItems: 'center', justifyContent: 'center', paddingVertical: 48, gap: 8 },
  emptyText: { fontSize: 13, fontFamily: FontFamily.regular, color: AdminColors.textMuted },

  // ── Team Average footer ──────────────────────────────────────────────────────
  // Mirrors HTML <tfoot> row: borderTop, slightly muted background, bold label
  tfootRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderTopWidth: 2,
    borderTopColor: AdminColors.borderTable,
    backgroundColor: AdminColors.bgPage,
  },
  // Label cell width = radio + avatar + all fixed columns combined.
  // Each fixed col's `width` already includes its own padding, so no extra
  // padding multiplication is needed here.
  tfootLabel: {
    width: RADIO_W + FIXED_COLS.reduce((s, c) => s + c.width, 0),
    paddingHorizontal: 10,
    paddingVertical: 10,
    justifyContent: 'center',
  },
  tfootLabelText: {
    fontSize: 14, fontWeight: '700', fontFamily: FontFamily.bold,
    color: AdminColors.textPrimary,
  },
  tfootCell: {
    paddingHorizontal: 10, paddingVertical: 10,
    alignItems: 'flex-end', justifyContent: 'center',
  },
  tfootValue: {
    fontSize: 14, fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary, textAlign: 'right',
  },
});

export default PeopleHubMyTeam;
